import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { User, Mail, LogOut, Camera, Pencil, Check, X, Building2, Users, Upload, Ship, ChevronDown, GraduationCap, Waves } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage } from '../utils/imageCompression';
import axios from 'axios';
import ESGBadges from './ESGBadges';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const USER_TYPES = [
  { value: 'volontario', label: 'Volontario', icon: User },
  { value: 'insegnante', label: 'Insegnante', icon: GraduationCap },
  { value: 'sub', label: 'Sub', icon: Waves },
  { value: 'peschereccio', label: 'Peschereccio', icon: Ship },
  { value: 'azienda', label: 'Azienda', icon: Building2 },
  { value: 'ente', label: 'Ente / Comune', icon: Building2 },
  { value: 'associazione', label: 'Associazione', icon: Users },
  { value: 'fondazione', label: 'Fondazione', icon: Building2 },
  { value: 'scuola', label: 'Scuola', icon: GraduationCap }
];

const ProfiloSection = ({ volunteerName, onNameChange }) => {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingType, setIsEditingType] = useState(false);
  const [newName, setNewName] = useState(volunteerName || '');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Nuovi campi profilo
  const [userType, setUserType] = useState('volontario');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const [teamSize, setTeamSize] = useState(1);
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [acceptsInvites, setAcceptsInvites] = useState(true);
  
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Carica profilo all'avvio
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.photo_url) {
          setPhotoPreview(response.data.photo_url);
          setProfilePhoto(response.data.photo_url);
        }
        if (response.data.user_type) {
          setUserType(response.data.user_type);
        }
        if (response.data.organization_name) {
          setOrganizationName(response.data.organization_name);
        }
        if (response.data.organization_logo) {
          setOrganizationLogo(response.data.organization_logo);
        }
        if (response.data.team_size) {
          setTeamSize(response.data.team_size);
        }
        if (response.data.accepts_invites !== undefined) {
          setAcceptsInvites(response.data.accepts_invites);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    if (token) loadProfile();
  }, [token]);

  const handlePhotoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 512, 0.7);
      setProfilePhoto(compressed.base64);
      setPhotoPreview(compressed.base64);
      
      // Salva automaticamente
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/profile`, 
        { photo_url: compressed.base64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Foto profilo aggiornata!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel salvataggio foto');
    } finally {
      setCompressing(false);
      setSaving(false);
    }
  }, [token]);

  const handleLogoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 256, 0.7);
      setOrganizationLogo(compressed.base64);
      
      // Salva automaticamente
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/profile`, 
        { organization_logo: compressed.base64 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Logo aggiornato!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel salvataggio logo');
    } finally {
      setCompressing(false);
      setSaving(false);
    }
  }, [token]);

  const saveName = useCallback(async () => {
    if (!newName.trim()) return;
    
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, 
        { name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onNameChange(newName.trim());
      setIsEditingName(false);
      toast.success('Nome aggiornato!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }, [newName, token, onNameChange]);

  const saveUserType = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, 
        { user_type: userType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsEditingType(false);
      toast.success('Tipo utente aggiornato!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }, [userType, token]);

  const saveOrganizationInfo = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/profile`, 
        { 
          user_type: userType,
          organization_name: organizationName.trim(),
          team_size: teamSize
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsEditingOrg(false);
      toast.success('Informazioni organizzazione aggiornate!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }, [userType, organizationName, teamSize, token]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logout effettuato');
    } catch (error) {
      toast.error('Errore nel logout');
    }
  };

  const needsOrganization = userType !== 'volontario';
  const userTypeLabel = USER_TYPES.find(t => t.value === userType)?.label || 'Volontario';

  return (
    <div className="max-w-4xl mx-auto p-6 pb-24">
      {/* Header Profilo */}
      <div className="text-center mb-8">
        {/* Foto Profilo */}
        <div className="relative inline-block mb-4">
          <div 
            className="w-24 h-24 rounded-full bg-slate-800 border-2 border-cyan-500 flex items-center justify-center overflow-hidden cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
          >
            {compressing ? (
              <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            ) : photoPreview ? (
              <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <User className="w-10 h-10 text-slate-400" />
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center rounded-full">
              <Camera className="w-6 h-6 text-white" />
            </div>
          </div>
          <input 
            ref={fileInputRef}
            type="file" 
            accept="image/*"
            onChange={handlePhotoSelect}
            className="hidden" 
          />
        </div>

        {/* Nome */}
        {isEditingName ? (
          <div className="flex items-center justify-center gap-2 mb-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="bg-slate-800 border border-cyan-500 rounded-lg px-3 py-2 text-white text-lg font-semibold text-center"
              autoFocus
            />
            <button onClick={saveName} disabled={saving} className="p-2 bg-emerald-500 rounded-lg text-white">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setIsEditingName(false); setNewName(volunteerName || ''); }} className="p-2 bg-red-500 rounded-lg text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 mb-2">
            <h2 className="text-2xl font-bold text-white">{volunteerName || 'Volontario'}</h2>
            <button onClick={() => setIsEditingName(true)} className="p-1 text-slate-400 hover:text-cyan-400">
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Email */}
        <div className="flex items-center justify-center gap-2 text-slate-400">
          <Mail className="w-4 h-4" />
          <span className="text-sm">{user?.email}</span>
        </div>

        {/* Tipo Utente - Modificabile */}
        {isEditingType ? (
          <div className="mt-3 flex flex-col items-center gap-2">
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="bg-slate-800 border border-cyan-500 rounded-lg px-4 py-2 text-white text-sm"
            >
              {USER_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <button onClick={saveUserType} disabled={saving} className="p-2 bg-emerald-500 rounded-lg text-white">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => setIsEditingType(false)} className="p-2 bg-red-500 rounded-lg text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-800 rounded-full border border-cyan-500/30">
              {userType === 'peschereccio' && <Ship className="w-4 h-4 text-orange-400" />}
              {userType === 'insegnante' && <GraduationCap className="w-4 h-4 text-lime-400" />}
              {userType === 'scuola' && <GraduationCap className="w-4 h-4 text-lime-400" />}
              {userType === 'sub' && <Waves className="w-4 h-4 text-blue-400" />}
              {!['volontario', 'peschereccio', 'insegnante', 'scuola', 'sub'].includes(userType) && <Building2 className="w-4 h-4 text-violet-400" />}
              {userType === 'volontario' && <User className="w-4 h-4 text-cyan-400" />}
              <span className="text-sm text-slate-300">{userTypeLabel}</span>
            </div>
            <button onClick={() => setIsEditingType(true)} className="p-1 text-slate-400 hover:text-cyan-400">
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Sezione Organizzazione */}
      {(needsOrganization || isEditingOrg) && (
        <div className="bg-slate-900/60 border border-violet-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-violet-400 uppercase tracking-wider flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Organizzazione
            </h3>
            {!isEditingOrg ? (
              <button onClick={() => setIsEditingOrg(true)} className="text-slate-400 hover:text-violet-400">
                <Pencil className="w-4 h-4" />
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={saveOrganizationInfo} disabled={saving} className="p-1 bg-emerald-500 rounded text-white">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setIsEditingOrg(false)} className="p-1 bg-red-500 rounded text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {isEditingOrg ? (
            <div className="space-y-4">
              {/* Tipo Utente Dropdown */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Tipo</label>
                <div className="relative">
                  <select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-violet-500 focus:outline-none appearance-none"
                  >
                    {USER_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {/* Nome Organizzazione */}
              {needsOrganization && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">
                    {userType === 'peschereccio' ? 'Nome Peschereccio' : 'Nome Organizzazione'}
                  </label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder={userType === 'peschereccio' ? 'Es. Nettuno I' : 'Es. Blue Ocean Foundation'}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                  />
                </div>
              )}

              {/* Numero Membri */}
              {needsOrganization && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Numero persone nell'organizzazione
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={teamSize}
                    onChange={(e) => setTeamSize(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-violet-500 focus:outline-none"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Logo e Nome */}
              <div className="flex items-center gap-4">
                {organizationLogo ? (
                  <img src={organizationLogo} alt="Logo" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    className="w-12 h-12 rounded-lg bg-slate-800 border-2 border-dashed border-slate-600 flex items-center justify-center hover:border-violet-500 transition-all"
                  >
                    <Upload className="w-5 h-5 text-slate-500" />
                  </button>
                )}
                <div>
                  <p className="text-white font-medium">{organizationName || 'Non specificato'}</p>
                  <p className="text-xs text-slate-400">{teamSize} {teamSize === 1 ? 'persona' : 'persone'}</p>
                </div>
              </div>
              <input 
                ref={logoInputRef}
                type="file" 
                accept="image/*"
                onChange={handleLogoSelect}
                className="hidden" 
              />
            </div>
          )}
        </div>
      )}

      {/* Mostra pulsante per aggiungere organizzazione se è volontario */}
      {!needsOrganization && !isEditingOrg && (
        <button
          onClick={() => setIsEditingOrg(true)}
          className="w-full mb-6 py-3 border-2 border-dashed border-slate-700 rounded-xl text-slate-400 hover:border-violet-500 hover:text-violet-400 transition-all flex items-center justify-center gap-2"
        >
          <Building2 className="w-4 h-4" />
          <span>Aggiungi organizzazione</span>
        </button>
      )}

      {/* Impostazioni Inviti */}
      <div className="mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-white font-medium text-sm">Ricevi inviti ad attività</p>
            <p className="text-xs text-slate-400 mt-1">
              Gli organizzatori potranno invitarti a partecipare a raccolte e attività
            </p>
          </div>
          <button
            onClick={async () => {
              const newValue = !acceptsInvites;
              setAcceptsInvites(newValue);
              try {
                await axios.put(`${BACKEND_URL}/api/profile`, 
                  { accepts_invites: newValue },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success(newValue ? 'Inviti attivati' : 'Inviti disattivati');
              } catch (error) {
                setAcceptsInvites(!newValue); // Ripristina in caso di errore
                toast.error('Errore nel salvataggio');
              }
            }}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              acceptsInvites ? 'bg-cyan-500' : 'bg-slate-600'
            }`}
          >
            <div 
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                acceptsInvites ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* ESG Badges */}
      <ESGBadges />

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="w-full py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl flex items-center justify-center gap-2 transition-all mt-6"
      >
        <LogOut className="w-5 h-5" />
        <span className="font-medium">Esci</span>
      </button>
    </div>
  );
};

export default ProfiloSection;
